import { api, type ApiResponse } from '../client';

// ================== TYPE DEFINITIONS ==================

// Dashboard Summary Data
export interface StatusBucketSummary {
  count: number;
  hours: number;
  value?: number;
}

export interface DashboardStatusBuckets {
  requested: StatusBucketSummary;
  approved: StatusBucketSummary & { value: number };
  rejected: StatusBucketSummary;
}

export interface DashboardSummary {
  totalHours: number;
  totalValue: number;
  totalDonationRequests: number;
  pendingRequests: number;
  pendingHours: number;
  totalHoursRequested: number;
  statusBuckets: DashboardStatusBuckets;
}

// Recent Donation Data
export interface RecentDonation {
  uuid: string;
  charity_name: string;
  employer_name: string;
  employer_top_up: number;
  leave_hours: number;
  leave_value: number;
  status: 'PAID' | 'APPROVED' | 'PENDING' | 'REJECTED';
  date: string; // ISO 8601 timestamp
}

// Dashboard Data Response
export interface DashboardData {
  summary: DashboardSummary;
  recent_donations: RecentDonation[];
}

// API Response Types
export interface EmployeeDashboardResponse {
  success: boolean;
  message: string;
  data: DashboardData;
}

export interface EmployeeDashboardErrorResponse {
  success: false;
  message: string;
  data: {
    errors?: {
      [key: string]: string[];
    };
  } | [];
}

// Request Parameters
export interface DashboardRequestParams {
  limit?: number; // Optional limit for recent donations (1-50)
}

// ================== EMPLOYEE DASHBOARD SERVICE ==================

export class EmployeeDashboardService {
  
  /**
   * Get employee dashboard data with summary statistics and recent donations
   * 
   * @param params Optional parameters including limit for recent donations
   * @returns Promise with dashboard data or error response
   */
  static async getDashboardData(params?: DashboardRequestParams): Promise<EmployeeDashboardResponse | EmployeeDashboardErrorResponse> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (params?.limit) {
        queryParams.append('limit', params.limit.toString());
      }
      
      const queryString = queryParams.toString();
      const url = `/dashboard/employee${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get<ApiResponse<DashboardData>>(url);
      
      return {
        success: true,
        message: response.data.message || 'Dashboard data retrieved successfully',
        data: response.data.data
      };
    } catch (error: any) {
      // Handle different types of errors
      if (error?.response?.status === 401) {
        return {
          success: false,
          message: 'User not authenticated',
          data: []
        };
      }
      
      if (error?.response?.status === 403) {
        return {
          success: false,
          message: 'User is not authorized to access employee dashboard',
          data: []
        };
      }
      
      if (error?.response?.status === 422) {
        return {
          success: false,
          message: 'Validation failed',
          data: {
            errors: error.response.data?.data?.errors || {}
          }
        };
      }
      
      // Generic error handling
      return {
        success: false,
        message: error?.response?.data?.message || 'Failed to retrieve dashboard data',
        data: []
      };
    }
  }

  /**
   * Get employee dashboard data with a specific limit of recent donations
   * 
   * @param limit Number of recent donations to return (1-50)
   * @returns Promise with dashboard data or error response
   */
  static async getDashboardDataWithLimit(limit: number): Promise<EmployeeDashboardResponse | EmployeeDashboardErrorResponse> {
    return this.getDashboardData({ limit });
  }

  /**
   * Get employee dashboard data with all recent donations (no limit)
   * 
   * @returns Promise with dashboard data or error response
   */
  static async getAllDashboardData(): Promise<EmployeeDashboardResponse | EmployeeDashboardErrorResponse> {
    return this.getDashboardData();
  }

  /**
   * Validate dashboard request parameters
   * 
   * @param params Request parameters to validate
   * @returns Validation result with errors if any
   */
  static validateDashboardParams(params: DashboardRequestParams): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (params.limit !== undefined) {
      if (!Number.isInteger(params.limit)) {
        errors.push('Limit must be an integer');
      } else if (params.limit < 1) {
        errors.push('Limit must be at least 1');
      } else if (params.limit > 50) {
        errors.push('Limit cannot exceed 50');
      }
    }

    return { valid: errors.length === 0, errors };
  }

  /**
   * Format currency values for display
   * 
   * @param value Numeric value to format
   * @param currency Currency code (default: AUD)
   * @returns Formatted currency string
   */
  static formatCurrency(value: number, currency: string = 'AUD'): string {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  }

  /**
   * Format date for display
   * 
   * @param dateString ISO 8601 date string
   * @returns Formatted date string (date only, no time)
   */
  static formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }).format(date);
    } catch (error) {
      return dateString; // Return original string if parsing fails
    }
  }

  /**
   * Get status color for UI display
   * 
   * @param status Donation status
   * @returns CSS color class or color value
   */
  static getStatusColor(status: RecentDonation['status']): string {
    switch (status) {
      case 'PAID':
        return '#10b981'; // Green
      case 'APPROVED':
        return '#3b82f6'; // Blue
      case 'PENDING':
        return '#f59e0b'; // Amber
      case 'REJECTED':
        return '#ef4444'; // Red
      default:
        return '#6b7280'; // Gray
    }
  }

  /**
   * Get status display text
   * 
   * @param status Donation status
   * @returns Human-readable status text
   */
  static getStatusText(status: RecentDonation['status']): string {
    switch (status) {
      case 'PAID':
        return 'Paid';
      case 'APPROVED':
        return 'Approved';
      case 'PENDING':
        return 'Pending';
      case 'REJECTED':
        return 'Rejected';
      default:
        return 'Unknown';
    }
  }
} 